/**
 * @file        src/modals/FareBreakupModal.jsx
 * @description Fare-breakup popup (the ⓘ "info" pin next to a price). Opened via
 *              nh:open-modal { name: 'fareBreakup', payload: { option, roomName,
 *              nights } }. Itemises the live v3 pricing the guest actually pays:
 *              base price, taxes & fees, discount, processing fee (our markup =
 *              pricing.DP) and the final total (pricing.FP). All values are real
 *              — nothing is fabricated, and we only show fields the API returns.
 * @author      Kinetika Labs
 * @created     2026-06-24
 * @changelog
 *   - 2026-10-09 Claude — The Convenience fee line (or invoice column) is hidden when the fee is ₹0, and shown as before when it is more. Display only; no backend change.
 *   - 2026-10-07 Claude — The convenience fee is asked for with the stay (optionId,
 *                          rooms, nights): a flat fee is per room, per night, and
 *                          its line says what it was multiplied by.
 *   - 2026-10-06 Claude — The offers and fee queries carry the hotel id, so a
 *                          Domestic/International rule (admin migration 076) is
 *                          previewed on this property as checkout will charge it.
 *   - 2026-10-05 Claude — Base price includes the processing fee; the Convenience fee row takes its place and is always shown.
 *                          Client decision: the Processing fee (our markup) is
 *                          no longer a line of its own — it is inside the Base
 *                          price, and the Convenience fee is shown in its place.
 *                          Display only: every total is unchanged.
 *   - 2026-10-05 Claude — Shows the admin auto DISCOUNT, an applied hotel coupon
 *                          and the convenience fee, priced by the shared
 *                          lib/hotelPricing.js (verbatim from HotelPayment), so
 *                          "Total payable" here is the total the payment page
 *                          charges. The supplier's own discount (pricing.discount,
 *                          already inside FP) is now labelled "Hotel discount" so
 *                          the two can never read as one line twice.
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-06-24 Claude - Expanded the single "Taxes & fees" line into the real
 *                          per-field breakdown (taxes / mf service fee / mft fee
 *                          tax / discount) with a reconciling residual line.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inr } from '../lib/format.js';
import { fetchHotelOffers, getHotelConvenienceFee } from '../api/hotels.js';
import { useBooking } from '../store/booking.store.js';
import { priceHotelCheckout } from '../lib/hotelPricing.js';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// "Convenience fee (2 rooms × 2 nights)" when a flat fee was multiplied by the stay.
function convenienceLabel(fee) {
  const name = fee?.name || 'Convenience fee';
  const u = fee?.perRoomNight;
  if (!u || u.rooms * u.nights <= 1) return name;
  return `${name} (${u.rooms} room${u.rooms > 1 ? 's' : ''} × ${u.nights} night${u.nights > 1 ? 's' : ''})`;
}

export default function FareBreakupModal({ open, onClose, payload }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const option = payload?.option || null;
  const roomInfo = option?.roomInfo?.[0] || {};
  const roomName = payload?.roomName || roomInfo.name || 'Room';
  const roomId = roomInfo.id || null;
  const nights = Math.max(1, payload?.nights || 1);
  const p = option?.pricing || {};
  // The room's marked-up price (FP) — what the admin discount and the
  // convenience fee are taken on, exactly as on the checkout/payment pages.
  const fpPrice = p.FP != null ? num(p.FP) : num(p.totalPrice) + (p.DP != null ? num(p.DP) : 0);

  // Audience-scoped (CLAUDE.md §9): keyed on the sign-in + role so a corporate
  // buyer never sees the retail rule. Hooks run before the early return.
  const loggedIn = useBooking((s) => s.loggedIn);
  const role = useBooking((s) => s.role) || '';
  // The hotel being viewed: Domestic/International fees and discounts are judged
  // by the server on its country, the same answer the checkout charges on.
  const hotelId = useBooking((s) => s.hotel?.id || '');
  const coupon = useBooking((s) => (s.appliedCoupon?.product === 'hotel' ? s.appliedCoupon : null));
  // A flat convenience fee is per room, per night — the stay this option was
  // priced on (the server's markupStay stamp, else the search).
  const searchRooms = useBooking((s) => s.hotelSearch?.rooms?.length || 0);
  const feeStay = {
    optionId: option?.optionId || '',
    rooms: p.markupStay?.rooms || searchRooms || '',
    nights: p.markupStay?.nights || nights,
  };
  const offersQ = useQuery({
    queryKey: ['hotel-offers', loggedIn, role, hotelId],
    queryFn: () => fetchHotelOffers(hotelId),
    enabled: Boolean(open),
    staleTime: 60_000,
  });
  const feeQ = useQuery({
    queryKey: ['hotel-convenience-fee', Math.round(fpPrice), loggedIn, role, hotelId, feeStay.optionId, feeStay.rooms, feeStay.nights],
    queryFn: () => getHotelConvenienceFee(fpPrice, hotelId, feeStay),
    enabled: Boolean(open) && fpPrice > 0,
    staleTime: 60_000,
  });

  if (!open) return null;

  // Every real pricing field, itemised (nothing fabricated, nothing collapsed):
  //   basePrice              → room rate before any tax/fee
  //   taxes                  → government taxes
  //   mf                     → supplier service / management fee
  //   mft                    → tax on that service fee
  //   discount               → supplier discount (subtracted)
  //   totalPrice             → supplier net total (base+taxes+mf+mft−discount)
  //   DP                     → OUR markup, billed as the "Processing fee" (FP − totalPrice)
  //   FP                     → final amount the guest pays
  //   gstClaimableAmount     → informational (shown as a footnote)
  // NOTE: DP is the markup / PROCESSING fee. The admin "Convenience fee"
  // (hotel_convenience_fees) is a SEPARATE charge added at payment — it is not DP.
  const base = num(p.basePrice);
  const taxes = num(p.taxes);
  const serviceFee = num(p.mf);
  const serviceFeeTax = num(p.mft);
  const discount = num(p.discount);
  const supplierTotal = num(p.totalPrice);
  const processing = p.DP != null ? num(p.DP) : Math.max(0, num(p.FP) - supplierTotal);
  const gstClaimable = num(p.gstClaimableAmount);
  const total = p.FP != null ? num(p.FP) : supplierTotal + processing;
  // Reconcile any residual the named fields don't capture, so the lines always
  // add up to the supplier total regardless of which keys the API populates.
  const otherFees =
    Math.round((supplierTotal - base - taxes - serviceFee - serviceFeeTax + discount) * 100) / 100;

  // Admin convenience fee + auto discount + coupon (lib/hotelPricing.js) — the
  // same lines HotelCheckout / HotelPayment print, so the discount is visible
  // from the room list on, not first at payment.
  const convenience = num(feeQ.data?.amount);
  const priced = priceHotelCheckout({ fp: total, convenienceFee: convenience, rules: offersQ.data?.hotelDiscounts, coupon });
  const rule = priced.discountRule;
  const ruleLabel = !rule ? '' : String(rule.discountType) === 'percentage'
    ? `${Number(rule.discountValue)}% off`
    : `${inr(Number(rule.discountValue))} flat`;

  const rows = [
    // Base price INCLUDES the processing fee (our markup, DP) — no own line.
    ['Base price', Math.round((base + processing) * 100) / 100],
    taxes > 0 ? ['Taxes', taxes] : null,
    serviceFee > 0 ? ['Service fee', serviceFee] : null,
    serviceFeeTax > 0 ? ['Service fee tax', serviceFeeTax] : null,
    otherFees > 0.5 ? ['Other taxes & fees', otherFees] : null,
    discount > 0 ? ['Hotel discount', -discount] : null,
    // Hidden when the convenience fee is ₹0.
    Number(convenience) > 0 ? [convenienceLabel(feeQ.data), convenience] : null,
    priced.discount > 0 ? [`${rule?.name || 'Discount'}${ruleLabel ? ` (${ruleLabel})` : ''}`, -priced.discount] : null,
    priced.couponDiscount > 0 ? [`Coupon (${coupon?.code})`, -priced.couponDiscount] : null,
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-md max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel p-6 no-scrollbar">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer" aria-label="Close">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>

        <h2 className="font-display text-xl font-extrabold pr-10">Fare Breakup</h2>

        {/* room meta */}
        <div className="rounded-xl border border-line bg-night/40 p-3 mt-4 text-sm space-y-1.5">
          <div className="flex justify-between gap-3">
            <span className="text-muted">Room type</span>
            <span className="font-semibold text-right">{roomName}</span>
          </div>
          {roomId && (
            <div className="flex justify-between gap-3">
              <span className="text-muted">Room ID</span>
              <span className="font-mono text-xs text-right">{roomId}</span>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <span className="text-muted">Stay</span>
            <span className="font-semibold">{nights} night{nights > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* itemised charges */}
        <div className="mt-4 divide-y divide-line">
          {rows.map(([label, amt]) => (
            <div key={label} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-muted">{label}</span>
              <span className={`font-semibold ${amt < 0 ? 'text-lime' : ''}`}>
                {amt < 0 ? `− ${inr(Math.abs(amt))}` : inr(amt)}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3">
            <span className="font-display font-bold">Total payable</span>
            <span className="font-display text-lg font-extrabold text-neon">{inr(priced.total)}</span>
          </div>
        </div>

        <p className="text-xs text-muted mt-1">Total for {nights} night{nights > 1 ? 's' : ''} · incl. all taxes &amp; fees</p>
        {gstClaimable > 0 && (
          <p className="text-xs text-muted mt-2">GST claimable amount: <b className="text-ink">{inr(gstClaimable)}</b></p>
        )}
      </div>
    </div>
  );
}
